/**
 * AudioAnalyzer — Extracts useful metrics from the Web Audio API AnalyserNode.
 *
 * Reads raw FFT frequency data and time-domain waveform data, then computes:
 *   - Bass / Mid / Treble energy bands (normalized 0-1)
 *   - Overall loudness
 *   - Beat detection (energy spike detection in low frequencies)
 *   - BPM estimation (based on intervals between detected beats)
 *
 * All values are temporally smoothed to prevent visual jitter.
 */

import { FrameData } from './types';

export class AudioAnalyzer {
  // ── Buffers reused every frame to avoid GC pressure ───────────────────────
  private frequencyData: Uint8Array;
  private timeDomainData: Uint8Array;

  // ── Smoothed energy values ────────────────────────────────────────────────
  private smoothBass = 0;
  private smoothMid = 0;
  private smoothTreble = 0;
  private smoothOverall = 0;

  // ── Beat detection state ──────────────────────────────────────────────────
  /** Rolling average of bass energy — beats are detected when current exceeds this */
  private bassAverage = 0;
  /** How many frames since last beat (cooldown prevents double-triggers) */
  private beatCooldown = 0;
  /** Current beat intensity, decays each frame */
  private beatIntensity = 0;

  // ── BPM estimation ────────────────────────────────────────────────────────
  /** Timestamps of recent beats for BPM calculation */
  private beatTimestamps: number[] = [];
  private estimatedBpm = 0;

  // ── Timing ────────────────────────────────────────────────────────────────
  private startTime = 0;
  private lastFrameTime = 0;

  /** Number of frequency bins (half of FFT size) */
  private readonly binCount: number;

  constructor(private analyser: AnalyserNode) {
    this.binCount = analyser.frequencyBinCount; // typically 1024
    this.frequencyData = new Uint8Array(new ArrayBuffer(this.binCount));
    this.timeDomainData = new Uint8Array(new ArrayBuffer(this.binCount));
    this.startTime = performance.now();
    this.lastFrameTime = this.startTime;
  }

  /**
   * Read current audio state and return a FrameData object.
   * Call this once per animation frame.
   */
  getFrameData(): FrameData {
    const now = performance.now();
    const deltaTime = now - this.lastFrameTime;
    this.lastFrameTime = now;

    // Read raw data from the AnalyserNode
    // Cast needed because TS 5.x tightened ArrayBuffer vs ArrayBufferLike
    this.analyser.getByteFrequencyData(this.frequencyData as unknown as Uint8Array<ArrayBuffer>);
    this.analyser.getByteTimeDomainData(this.timeDomainData as unknown as Uint8Array<ArrayBuffer>);

    // ── Calculate energy in 3 frequency bands ───────────────────────────────
    // The frequency bins map linearly from 0 Hz to sampleRate/2 (typically 22050 Hz).
    // With 1024 bins, each bin spans ~21.5 Hz.
    //   Bass:   bins 0-11   → ~0-250 Hz
    //   Mids:   bins 12-185 → ~250-4000 Hz
    //   Treble: bins 186+   → ~4000-22050 Hz
    const bassEnd = Math.floor(this.binCount * 250 / 22050);
    const midEnd = Math.floor(this.binCount * 4000 / 22050);

    const rawBass = this.bandEnergy(0, bassEnd);
    const rawMid = this.bandEnergy(bassEnd, midEnd);
    const rawTreble = this.bandEnergy(midEnd, this.binCount);
    const rawOverall = this.bandEnergy(0, this.binCount);

    // Smooth using exponential moving average (lerp toward target)
    // Factor 0.15 = responsive but smooth. Higher = more responsive, lower = smoother.
    const smoothFactor = 0.15;
    this.smoothBass += (rawBass - this.smoothBass) * smoothFactor;
    this.smoothMid += (rawMid - this.smoothMid) * smoothFactor;
    this.smoothTreble += (rawTreble - this.smoothTreble) * smoothFactor;
    this.smoothOverall += (rawOverall - this.smoothOverall) * smoothFactor;

    // ── Beat detection ──────────────────────────────────────────────────────
    // A beat is a sudden spike in bass energy above the rolling average.
    // We use a cooldown to prevent rapid re-triggers.
    const beatThreshold = 1.4; // bass must be 40% above average to be a beat
    const minCooldownFrames = 8; // ~130ms at 60fps — prevents double-triggers

    // Update rolling average of bass energy (slow decay)
    this.bassAverage += (rawBass - this.bassAverage) * 0.05;

    let isBeat = false;
    if (this.beatCooldown <= 0 && rawBass > this.bassAverage * beatThreshold && rawBass > 0.15) {
      isBeat = true;
      this.beatIntensity = Math.min(1, (rawBass - this.bassAverage) / this.bassAverage);
      this.beatCooldown = minCooldownFrames;

      // Record beat timestamp for BPM calculation
      this.beatTimestamps.push(now);
      // Keep only last 20 beats (enough for stable BPM)
      if (this.beatTimestamps.length > 20) {
        this.beatTimestamps.shift();
      }
      this.updateBpm();
    } else {
      this.beatCooldown = Math.max(0, this.beatCooldown - 1);
      // Decay beat intensity smoothly
      this.beatIntensity *= 0.92;
    }

    return {
      frequencyData: this.frequencyData,
      timeDomainData: this.timeDomainData,
      bassEnergy: this.smoothBass,
      midEnergy: this.smoothMid,
      trebleEnergy: this.smoothTreble,
      overallEnergy: this.smoothOverall,
      isBeat,
      beatIntensity: this.beatIntensity,
      bpm: this.estimatedBpm,
      timestamp: now,
      deltaTime,
      elapsed: now - this.startTime,
    };
  }

  /**
   * Calculate normalized energy (0-1) for a range of frequency bins.
   * Energy = average amplitude / 255 (max value for byte frequency data).
   */
  private bandEnergy(startBin: number, endBin: number): number {
    let sum = 0;
    const count = endBin - startBin;
    if (count <= 0) return 0;
    for (let i = startBin; i < endBin; i++) {
      sum += this.frequencyData[i];
    }
    return sum / (count * 255);
  }

  /**
   * Estimate BPM from the intervals between recent detected beats.
   * Uses the median interval for robustness against outliers.
   */
  private updateBpm(): void {
    if (this.beatTimestamps.length < 4) return;

    const intervals: number[] = [];
    for (let i = 1; i < this.beatTimestamps.length; i++) {
      intervals.push(this.beatTimestamps[i] - this.beatTimestamps[i - 1]);
    }

    // Use median interval (more robust than mean against outliers)
    intervals.sort((a, b) => a - b);
    const median = intervals[Math.floor(intervals.length / 2)];

    if (median > 0) {
      // BPM = 60,000 ms / interval between beats
      const bpm = 60000 / median;
      // Clamp to reasonable range (60-200 BPM)
      if (bpm >= 60 && bpm <= 200) {
        // Smooth the BPM estimate
        this.estimatedBpm += (bpm - this.estimatedBpm) * 0.2;
      }
    }
  }
}
